import ModalImpressao from '@/components/shared/ModalImpressao';
import SessaoFormatter from '@/domain/sessao/SessaoFormatter';
import SystemUtil from '@/util/SystemUtil';
import { Warning } from '@mui/icons-material';
import SegredoJustica from '@mui/icons-material/Lock';
import { Alert, Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '../../../../util/Configuracao';
import IconeOrigem from '../../../IconeOrigem';
import ReactComponent from '../../../ReactComponent';

class ModalRelatorioProcessos extends ReactComponent {
    /** @type {boolean} */
    carregandoInformacoesProcessos = true;

    /** @type {object[]} */
    informacoesProcessos = [];

    /** @type {string[]} */
    processosNaoListadosNoRelatorio = [];

    dataGeracao = new Date();

    constructor(props) {
        super(props);

        makeObservable(this, {
            dataGeracao: observable,
            getDataGeracao: computed,
            carregandoInformacoesProcessos: observable,
            informacoesProcessos: observable,
            processosNaoListadosNoRelatorio: observable,
            iniciaCarregamentoDeInformacoesDosProcessos: action,
            carregarInformacoesProcessos: action,
            titulo: computed,
            totalDeProcessos: computed,
            totalDeProcessosNoRelatorio: computed,
            mensagemProcessosNaoListadosNoRelatorio: computed,
            cabecalho: computed,
            listaProcessosRequisicao: computed,
            listaNumerosProcessos: computed,
            processos: computed
        });
    }

    componentDidMount() {
        this.iniciaCarregamentoDeInformacoesDosProcessos();
    }

    iniciaCarregamentoDeInformacoesDosProcessos = () => {
        if (this.listaProcessosRequisicao.length > 0) {
            this.carregarInformacoesProcessos();
        } else {
            this.carregandoInformacoesProcessos = false;
        }
    };

    carregarInformacoesProcessos = async () => {
        try {
            const resultado = await this.sessaoService.recuperarInformacoesRelatorioProcessos(
                this.sessaoStore.sessaoSelecionada.id,
                this.listaProcessosRequisicao
            );

            runInAction(() => {
                this.informacoesProcessos = resultado;
                const processosNoRelatorio = new Set(resultado.map((processo) => processo.numeroProcesso));
                this.processosNaoListadosNoRelatorio = this.listaNumerosProcessos.filter(
                    (numeroProcesso) => !processosNoRelatorio.has(numeroProcesso)
                );
            });
        } catch (error) {
            console.error(error);
            this.observableProps.onCloseModalImpressao();
        } finally {
            runInAction(() => {
                this.carregandoInformacoesProcessos = false;
            });
        }
    };

    get titulo() {
        let titulo = 'Planilha de julgamento';

        if (this.sessaoStore.sessaoSelecionada) {
            const { tipo, tipoJulgamento, orgaoJudicante, prazos } = this.sessaoStore.sessaoSelecionada;
            titulo += ' - ';
            titulo += tipo === Configuracao.TIPO_PAUTA_ORDINARIA ? 'Sessão Ordinária' : 'Sessão Extraordinária';

            titulo += ' - ';
            titulo += orgaoJudicante.descricao;

            titulo += ' - ';
            titulo += SessaoFormatter.formatarDataSessao(tipoJulgamento, prazos);

            titulo += ' - ';
            titulo += this.sessaoStore.sessaoSelecionada.descricao;
        }

        return titulo;
    }

    get totalDeProcessos() {
        return 'Total de processos: ' + this.processos.length;
    }

    get totalDeProcessosNoRelatorio() {
        return 'Processos no relatório: ' + this.informacoesProcessos.length;
    }

    get mensagemProcessosNaoListadosNoRelatorio() {
        const totalProcessosNaoListados = this.processos.length - this.informacoesProcessos.length;
        let mensagem = `${totalProcessosNaoListados} processos não listados pois não foram importados para a sala de sessão`;

        if (totalProcessosNaoListados === 1) {
            mensagem = '1 processo não listado pois não foi importado para a sala de sessão';
        }

        return mensagem;
    }

    get getDataGeracao() {
        return 'Relatório gerado em ' + dayjs(this?.dataGeracao).format('DD/MM/YYYY HH:mm');
    }

    get cabecalho() {
        const exibirAviso = !this.carregandoInformacoesProcessos && this.processosNaoListadosNoRelatorio.length > 0;
        return (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography sx={{ justifyContent: 'center', fontWeight: 'bold' }}>{this.titulo}</Typography>
                <Typography sx={{ justifyContent: 'center' }}>{this.getDataGeracao}</Typography>
                {!this.carregandoInformacoesProcessos && (
                    <Typography sx={{ justifyContent: 'center' }}>
                        {this.totalDeProcessos} / {this.totalDeProcessosNoRelatorio}
                    </Typography>
                )}
                {!!exibirAviso && (
                    <Alert
                        severity="warning"
                        sx={{ mt: 1, justifyContent: 'center' }}
                    >
                        {this.mensagemProcessosNaoListadosNoRelatorio}. Veja a lista completa ao final do relatório.
                    </Alert>
                )}
            </Box>
        );
    }

    get listaProcessosRequisicao() {
        return this.processos
            .filter((processo) => !processo.necessitaImportacaoSalaSessao)
            .map(({ processo, sessaoId }) => ({
                numeroProcesso: processo.numeroCompleto,
                sessaoId
            }));
    }

    get listaNumerosProcessos() {
        return this.processos.map(({ processo }) => processo.numeroCompleto);
    }

    get processos() {
        return (
            this.store.opcoesProcessosEmExibicao
                .find((opcaoProcessos) => opcaoProcessos.id === 'P')
                .funcaoProcessosFiltrados() || []
        );
    }

    renderTableHead() {
        return (
            <TableHead>
                <TableRow>
                    <TableCell
                        align="center"
                        sx={{ minWidth: '320px', borderTop: '1px solid rgba(224, 224, 224, 1)' }}
                    >
                        <Typography fontWeight={'bold'}>Informações do processo</Typography>
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={{
                            border: '1px solid rgba(224, 224, 224, 1)'
                        }}
                    >
                        <Typography fontWeight={'bold'}>Ementa</Typography>
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
                    >
                        <Typography fontWeight={'bold'}>Decisão</Typography>
                    </TableCell>
                </TableRow>
            </TableHead>
        );
    }

    renderTableBody() {
        return (
            <TableBody>
                {
                    // @ts-ignore
                    this.informacoesProcessos?.map(
                        ({ numeroProcesso, relator, partesProcesso, ementa, decisao, observacao }) => (
                            <TableRow
                                key={numeroProcesso}
                                sx={{ verticalAlign: 'top' }}
                            >
                                <TableCell
                                    style={{ minWidth: '320px' }}
                                    align="left"
                                >
                                    <Typography sx={{ textDecoration: 'underline' }}>Número do processo:</Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>{numeroProcesso}</Typography>
                                    <br />
                                    <Typography sx={{ textDecoration: 'underline' }}>Relator:</Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>{relator?.nome}</Typography>
                                    <br />
                                    <Typography sx={{ textDecoration: 'underline' }}>Partes Processuais:</Typography>
                                    <Typography
                                        dangerouslySetInnerHTML={{
                                            __html: partesProcesso
                                                ?.map((parteProcesso) => {
                                                    return `<i>${parteProcesso.denominacao}:</i> ${parteProcesso?.partes?.map((parte) => parte?.parte?.nome).join(', ')}<br/><br/>`;
                                                })
                                                .join('')
                                        }}
                                    />
                                    <br />
                                    <Typography sx={{ textDecoration: 'underline' }}>Observação:</Typography>
                                    <Typography>{observacao}</Typography>
                                    <br />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        borderLeft: '1px solid rgba(224, 224, 224, 1)',
                                        borderRight: '1px solid rgba(224, 224, 224, 1)'
                                    }}
                                >
                                    <Typography>{this.preSessaoStore.retirarTagsHtmlDaEmenta(ementa)}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography>{this.preSessaoStore.retirarTagsHtmlDaDecisao(decisao)}</Typography>
                                </TableCell>
                            </TableRow>
                        )
                    )
                }
            </TableBody>
        );
    }

    renderTableFooter() {
        return (
            this.processosNaoListadosNoRelatorio.length > 0 && (
                <TableRow>
                    <TableCell colSpan={3}>
                        <Typography>
                            {this.mensagemProcessosNaoListadosNoRelatorio}:{' '}
                            {this.processosNaoListadosNoRelatorio.join(', ')}
                        </Typography>
                    </TableCell>
                </TableRow>
            )
        );
    }

    renderMensagem() {
        const mensagem =
            this.listaProcessosRequisicao.length > 0
                ? 'Não foi possível buscar as informações dos processos da sessão.'
                : 'Nenhum processo está disponível para geração do relatório.';
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', my: 2 }}>
                <Warning sx={{ fill: 'darkorange' }} />
                <Typography sx={{ color: 'darkorange', fontWeight: 'bold', marginLeft: '8px', marginTop: '2px' }}>
                    {mensagem}
                </Typography>
            </Box>
        );
    }

    renderTable() {
        return this.informacoesProcessos.length > 0 ? (
            <Table
                sx={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}
                stickyHeader
            >
                {this.renderTableHead()}
                {this.renderTableBody()}
                {this.renderTableFooter()}
            </Table>
        ) : (
            this.renderMensagem()
        );
    }

    exportarCsv() {
        const separador = ';';
        const cabecalhoLinha = [
            'Número do processo',
            'Relator',
            'Partes Processuais',
            'Ementa',
            'Decisão',
            'Observação'
        ].join(separador);

        const linhas = this.informacoesProcessos?.map((info) => {
            const itens = [
                info.numeroProcesso,
                info.relator.sigla,
                info.partesProcesso.map((p) => p.partes.map((pa) => p.denominacao + ': ' + pa.parte.nome).join(', ')),
                this.preSessaoStore.retirarTagsHtmlDaEmenta(info.ementa),
                this.preSessaoStore.retirarTagsHtmlDaDecisao(info.decisao),
                info.observao ?? ''
            ];

            return itens.map((i) => `"${(i ?? '').toString().replaceAll('"', '""')}"`).join(separador);
        });
        const conteudoCsv = [cabecalhoLinha, ...linhas].join('\n');
        SystemUtil.exportarAquivoCsv('Relatório', conteudoCsv);
    }

    renderNumeroProcesso(registro) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                }}
            >
                <Typography
                    display={'inline'}
                    sx={{ fontWeight: 'bold' }}
                >
                    {registro.processo.numeroCompleto}
                </Typography>
                {!!this.parametroStore.isOrgaoTST && registro.processo?.origem === Configuracao.ORIGEM_PJE && (
                    <Box sx={{ ml: 1 }}>
                        <IconeOrigem processo={registro.processo} />
                    </Box>
                )}
                {!!registro.segredoJustica && (
                    <Box sx={{ ml: 1 }}>
                        <Tooltip title={<Typography>SEGREDO DE JUSTIÇA</Typography>}>
                            <SegredoJustica />
                        </Tooltip>
                    </Box>
                )}
            </Box>
        );
    }

    render() {
        return (
            <ModalImpressao
                open={this.observableProps.openModalImpressao}
                onClose={this.observableProps.onCloseModalImpressao}
                titulo={this.titulo}
                cabecalho={this.cabecalho}
                carregando={this.carregandoInformacoesProcessos}
                conteudo={this.renderTable()}
                exportar={this.exportarCsv.bind(this)}
            />
        );
    }
}

export default inject('stores')(observer(ModalRelatorioProcessos));
